<template>
  <LISAMenu
    v-if="event.deletedAt == null && isLISA3"
    :items="formattedMenuItems"
    :placement="'right-start'"
  >
    <template #trigger="{ triggerAttrs }">
      <LISAButton v-bind="triggerAttrs" class="p-0" icon="fas fa-plus-square" text> </LISAButton>
    </template>
  </LISAMenu>
  <component
    :is="modalComponent"
    v-if="modalComponent != null && event.deletedAt == null && isLISA3"
    ref="modalComponentRef"
    :event-id="props.event.id"
    :event-type="props.event.__typename"
    :visible="true"
    :model-value="true"
    v-bind="{ ...$props, ...modalComponentAdditionalBind }"
    @update:visible-action-modal="hideModal"
  >
    <slot v-bind="$props" />
  </component>
  <div v-if="event.deletedAt == null && !isLISA3">
    <LISAButton
      style="border: 0"
      type="button"
      :disabled="loadingWorkflow"
      :style="
        changeColorAndBackgroundOnToggle ? 'background: var(--p-primary-color);color: white' : ''
      "
      class="p-button-sm p-button-outlined p-2"
      @click="toggle"
    >
      <i class="far fa-plus-square" />
    </LISAButton>
    <PrimeVueMenu
      ref="menu"
      class="EventButtonList"
      :model="menuItems"
      popup
      @show="changeColorAndBackgroundOnToggle = !changeColorAndBackgroundOnToggle"
      @hide="changeColorAndBackgroundOnToggle = !changeColorAndBackgroundOnToggle"
    />
    <component
      :is="modalComponent"
      v-if="modalComponent != null"
      ref="modalComponentRef"
      :event-id="props.event.id"
      :event-type="props.event.__typename"
      :visible="true"
      :model-value="true"
      v-bind="{ ...$props, ...modalComponentAdditionalBind }"
      @update:visible-action-modal="hideModal"
    >
      <slot v-bind="$props" />
    </component>
  </div>
</template>

<script setup lang="ts">
import { type Component, computed, inject, type PropType, ref, markRaw, nextTick } from "vue";
import PrimeVueMenu from "primevue/menu";
import LISAButton from "@LISAPrime/LISAButton.vue";
import type { MenuItem } from "primevue/menuitem";
import { useTranslation } from "@/language/translation";
import {
  useEABShorthandsAndWorkflowForBoardsQuery,
  type EventListEventFieldsFragment,
} from "@/graphql";
import CreateTaskButton from "./CreateTaskButton";
import CreateA3Button from "./CreateA3Button";
import CreateA5Button from "./CreateA5Button";
import CreateKaizenButton from "./CreateKaizenButton";
import CreateIHOrderButton from "@/modules/ih/components/CreateIHOrderButton";
import EABShorthandButton from "@/modules/sqdc/components/EABShorthandActions/EABShorthandButton.vue";
import type { ActionButtonEntryType, ActionButtonType } from "./ActionButton";
import router from "@/router";
import { BoardIdInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import { useIHConfig, useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { LISAMenu, type LISAMenuGroup, type LISAMenuItem } from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3";

const staticActionButtonList: ActionButtonType[] = [
  CreateTaskButton,
  CreateA3Button,
  CreateA5Button,
  CreateKaizenButton,
];

const props = defineProps({
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
  actions: {
    type: Array as PropType<ActionButtonEntryType[] | null>,
    required: false,
    default: null,
  },
  shorthands: {
    type: Array as PropType<ActionButtonEntryType[] | null>,
    required: false,
    default: null,
  },
});
const { t } = useTranslation();

const boardId = inject(BoardIdInjectionKey, ref<number | null>(null));

const boardIdForWorkflows = computed<number | undefined>(
  () =>
    boardId.value ??
    props.event.boardConnection.find((con) => con.status === 0)?.boardId ??
    props.event.boardConnection.toSorted((a, b) => a.createdAt.localeCompare(b.createdAt))[0]
      ?.boardId,
);

const { result: resultWorkflow, loading: loadingWorkflow } =
  useEABShorthandsAndWorkflowForBoardsQuery(
    computed(() => ({
      boardId: boardIdForWorkflows.value!,
    })),
    computed(() => ({
      enabled: boardIdForWorkflows.value != null,
      fetchPolicy: "cache-first",
      errorPolicy: "ignore",
    })),
  );

const { siteId: siteIdForWorkflows } = useSpecifiedBoardContext(boardIdForWorkflows);

const eabShorthands = computed(() => {
  return resultWorkflow.value?.GetEABShorthandsForBoard ?? [];
});

const eabWorkflows = computed(() => {
  return resultWorkflow.value?.WFGetWorkflowsForBoard ?? [];
});

const ihConfig = useIHConfig(siteIdForWorkflows);

const actionButtonList = computed(() => {
  if (props.actions != null) {
    return props.actions;
  }
  const list = staticActionButtonList;
  if (
    ihConfig?.resultIHConfig.value?.unlockMaintenanceOrder === true &&
    ihConfig?.resultIHConfig.value?.technicalPlaceMaintained === true &&
    !list.includes(CreateIHOrderButton)
  ) {
    list.push(CreateIHOrderButton);
  }

  return list;
});

const modalComponent = ref<Component>();
const modalComponentRef = ref<
  Component & {
    createShorthandActionFunction?: () => void;
  }
>();
const modalComponentAdditionalBind = ref<Record<string, any>>({});
const changeColorAndBackgroundOnToggle = ref(false);

const shorthandButtonList = computed<MenuItem[]>(() => {
  return (
    props.shorthands?.map((shorthand) => ({
      label: shorthand.shorthand?.name,
      icon: "far fa-comment-dots text-primary",
      items: [],
      command: () => {
        modalComponent.value = undefined;
        nextTick(() => {
          modalComponent.value = markRaw(EABShorthandButton);
          modalComponentAdditionalBind.value = {
            shorthand,
          };
          nextTick(() => {
            modalComponentRef.value?.createShorthandActionFunction?.();
          });
        });
      },
      callback: () => {
        modalComponent.value = undefined;
        nextTick(() => {
          modalComponent.value = markRaw(EABShorthandButton);
          modalComponentAdditionalBind.value = {
            shorthand,
          };
          nextTick(() => {
            modalComponentRef.value?.createShorthandActionFunction?.();
          });
        });
      },
    })) ?? [
      ...(eabShorthands?.value?.map((shorthand) => ({
        label: shorthand.name,
        icon: "far fa-comment-dots text-primary",
        items: [],
        callback: () => {
          modalComponent.value = undefined;
          nextTick(() => {
            modalComponent.value = markRaw(EABShorthandButton);
            modalComponentAdditionalBind.value = {
              shorthand,
            };
            nextTick(() => {
              modalComponentRef.value?.createShorthandActionFunction?.();
            });
          });
        },
        command: () => {
          modalComponent.value = undefined;
          nextTick(() => {
            modalComponent.value = markRaw(EABShorthandButton);
            modalComponentAdditionalBind.value = {
              shorthand,
            };
            nextTick(() => {
              modalComponentRef.value?.createShorthandActionFunction?.();
            });
          });
        },
      })) ?? []),
    ]
  );
});

const workflowButtonList = computed<MenuItem[]>(() => {
  return (
    eabWorkflows?.value
      .map((workflow): MenuItem => {
        const startNode = workflow.drawFlowNodes.find((node) => node.isStartNode);
        if (startNode == null) {
          throw Error("startNode missing");
        }
        return {
          label: workflow.name,
          icon: "far fa-comments text-primary",
          shorthand: startNode?.shorthand,
          items: [],
          callback: () => {
            modalComponent.value = undefined;
            nextTick(() => {
              modalComponent.value = markRaw(EABShorthandButton);
              modalComponentAdditionalBind.value = {
                shorthand: startNode?.shorthand,
                workflow: {
                  id: workflow.id,
                  name: workflow.name,
                  description: workflow.description,
                  node: startNode,
                },
              };
              nextTick(() => {
                modalComponentRef.value?.createShorthandActionFunction?.();
              });
            });
          },
          command: () => {
            modalComponent.value = undefined;
            nextTick(() => {
              modalComponent.value = markRaw(EABShorthandButton);
              modalComponentAdditionalBind.value = {
                shorthand: startNode?.shorthand,
                workflow: {
                  id: workflow.id,
                  name: workflow.name,
                  description: workflow.description,
                  node: startNode,
                },
              };
              nextTick(() => {
                modalComponentRef.value?.createShorthandActionFunction?.();
              });
            });
          },
        };
      })
      .filter((workflow) => workflow.shorthand != null) ?? []
  );
});

function hideModal(visible: boolean) {
  if (!visible) {
    modalComponent.value = undefined;
    modalComponentAdditionalBind.value = {};
  }
}

const formattedMenuItems = computed<LISAMenuGroup[]>(() =>
  menuItems.value
    .filter((item) => item.items != null)
    .map((item) => ({
      ...item,
      label: item.label as string,
      items: item.items as LISAMenuItem[],
    })),
);

const menuItems = computed<MenuItem[]>(() => {
  if (loadingWorkflow.value) {
    return [];
  }
  const entries: MenuItem[] = [
    {
      label: t("components.event-action.event.action-buttons.EventActionButtonList.createAction"),
      items: actionButtonList.value.map((button) => {
        if (button.modalComponent != null) {
          return {
            label: t(button.label),
            icon: `${button.icon ?? "far fa-comment"} text-primary ${button.modalComponent.name}`,
            command: () => {
              modalComponentAdditionalBind.value = button.modalComponentAdditionalBind || {};
              modalComponent.value = undefined;
              nextTick(() => {
                modalComponent.value = markRaw(button.modalComponent!);
              });
            },
            callback: () => {
              modalComponentAdditionalBind.value = button.modalComponentAdditionalBind || {};
              modalComponent.value = undefined;
              nextTick(() => {
                modalComponent.value = markRaw(button.modalComponent!);
              });
            },
          };
        }

        return {
          label: t(button.label),
          icon: `${button.icon ?? "far fa-comment"} text-primary ${button.name}`,
          disabled: button.disabled === true,
          command:
            button.to != null
              ? () => {
                  router.push(button.to!(props.event.id, props.event.__typename));
                }
              : button.command,
          callback:
            button.to != null
              ? () => {
                  router.push(button.to!(props.event.id, props.event.__typename));
                }
              : button.command,
        };
      }),
    },
  ];
  if (shorthandButtonList.value.length > 0 && isLISA3.value === false) {
    entries.push({
      separator: true,
    });
  }
  if (shorthandButtonList.value.length > 0) {
    entries.push({
      label: t(
        "components.event-action.event.action-buttons.EventActionButtonList.createShorthand",
      ),

      items: shorthandButtonList.value,
    });
  }
  if (workflowButtonList.value.length > 0) {
    entries.push({
      separator: true,
    });
  }
  if (workflowButtonList.value.length > 0) {
    entries.push({
      label: t("components.event-action.event.action-buttons.EventActionButtonList.createWorkflow"),
      items: workflowButtonList.value,
    });
  }
  return entries;
});

const menu = ref<InstanceType<typeof PrimeVueMenu>>();

const toggle = (event: Event) => {
  menu.value?.toggle(event);
};

// TODO: kill it (This should not be in here)
async function openIHOrderModal() {
  const component = actionButtonList.value?.find((el) => el.isIHOrderButton);
  if (component) {
    modalComponent.value = undefined;
    await nextTick();
    modalComponent.value = markRaw(CreateIHOrderButton.modalComponent!);
  }
}

defineExpose({ openIHOrderModal });
</script>

<style lang="scss">
@use "./EventButton.scss" as *;
</style>
